import React, { useEffect, useMemo, useState } from "react";
import "./CalendarWidget.css";

const readTimezone = () =>
  localStorage.getItem("timezone") ||
  Intl.DateTimeFormat().resolvedOptions().timeZone;

function getZonedYMD(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const get = (t) => parts.find((p) => p.type === t)?.value;
  return {
    y: Number(get("year")),
    m: Number(get("month")),
    d: Number(get("day")),
  };
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function weekdayIndexMon0(y, m, d, timeZone) {
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const wd = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    weekday: "short",
  }).format(dt);
  const map = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  return map[wd] ?? 0;
}

function daysInMonth(y, m) {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function monthLabel(y, m, timeZone) {
  const dt = new Date(Date.UTC(y, m - 1, 1, 12, 0, 0));
  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    month: "long",
    year: "numeric",
  }).format(dt);
}

function weekdayHeader() {
  return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
}

function isoWeekNumber(y, m, d) {
  const date = new Date(Date.UTC(y, m - 1, d));
  const dayNum = (date.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  date.setUTCDate(date.getUTCDate() - dayNum + 3); // to Thursday
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
  const week =
    1 +
    Math.round(
      (date.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000)
    );
  return week;
}

function buildMonthGrid(y, m, timeZone) {
  const firstIdx = weekdayIndexMon0(y, m, 1, timeZone); // 0..6 Mon..Sun
  const dim = daysInMonth(y, m);

  const cells = [];
  for (let i = 0; i < firstIdx; i++) cells.push(null);
  for (let day = 1; day <= dim; day++) cells.push({ y, m, d: day });
  while (cells.length % 7 !== 0) cells.push(null);

  return cells;
}

function msUntilNextMidnightInTZ(timeZone) {
  const now = new Date();
  const today = getZonedYMD(now, timeZone);

  const isAfterMidnight = (d) => {
    const ymd = getZonedYMD(d, timeZone);
    return ymd.y !== today.y || ymd.m !== today.m || ymd.d !== today.d;
  };

  let lo = now.getTime();
  let hi = now.getTime() + 36 * 3600 * 1000;


  if (!isAfterMidnight(new Date(hi))) {
    hi = now.getTime() + 48 * 3600 * 1000;
  }

  while (hi - lo > 1000) {
    const mid = Math.floor((lo + hi) / 2);
    if (isAfterMidnight(new Date(mid))) hi = mid;
    else lo = mid;
  }


  return Math.max(0, hi - now.getTime()) + 250;
}

export default function CalendarWidget() {
  const [timeZone, setTimeZone] = useState(readTimezone());


  useEffect(() => {
    const t = setInterval(() => {
      const tz = readTimezone();
      setTimeZone((prev) => (prev === tz ? prev : tz));
    }, 1500);
    return () => clearInterval(t);
  }, []);


  const [midnightPulse, setMidnightPulse] = useState(0);

  useEffect(() => {
    let timeoutId;

    const schedule = () => {
      const ms = msUntilNextMidnightInTZ(timeZone);
      timeoutId = setTimeout(() => {
        setMidnightPulse((x) => x + 1);
        schedule();
      }, ms);
    };

    schedule();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [timeZone]);

  const today = useMemo(() => {
    void midnightPulse;
    return getZonedYMD(new Date(), timeZone);
  }, [timeZone, midnightPulse]);

  const [viewY, setViewY] = useState(today.y);
  const [viewM, setViewM] = useState(today.m);

  useEffect(() => {
    setViewY(today.y);
    setViewM(today.m);
  }, [timeZone, today.y, today.m]);

  const [selected, setSelected] = useState({
    y: today.y,
    m: today.m,
    d: today.d,
  });

  useEffect(() => {
    setSelected({ y: today.y, m: today.m, d: today.d });
  }, [today.y, today.m, today.d]);

  const [open, setOpen] = useState(false);

  const monthTitle = useMemo(
    () => monthLabel(viewY, viewM, timeZone),
    [viewY, viewM, timeZone]
  );
  const grid = useMemo(
    () => buildMonthGrid(viewY, viewM, timeZone),
    [viewY, viewM, timeZone]
  );

  const isoWeek = useMemo(
    () => isoWeekNumber(today.y, today.m, today.d),
    [today.y, today.m, today.d]
  );

  const dayName = useMemo(() => {
    const dt = new Date(Date.UTC(today.y, today.m - 1, today.d, 12, 0, 0));
    return new Intl.DateTimeFormat("en-GB", {
      timeZone,
      weekday: "long",
    }).format(dt);
  }, [today.y, today.m, today.d, timeZone]);

  const monthYear = useMemo(
    () => monthLabel(today.y, today.m, timeZone),
    [today.y, today.m, timeZone]
  );

  function prevMonth() {
    const m = viewM - 1;
    if (m < 1) {
      setViewM(12);
      setViewY(viewY - 1);
    } else setViewM(m);
  }

  function nextMonth() {
    const m = viewM + 1;
    if (m > 12) {
      setViewM(1);
      setViewY(viewY + 1);
    } else setViewM(m);
  }

  function goToday() {
    setViewY(today.y);
    setViewM(today.m);
    setSelected({ y: today.y, m: today.m, d: today.d });
  }

  function isTodayCell(c) {
    return c && c.y === today.y && c.m === today.m && c.d === today.d;
  }

  function isSelectedCell(c) {
    return c && c.y === selected.y && c.m === selected.m && c.d === selected.d;
  }

  return (
    <>
      {/* MINI CARD */}
      <div className="cw">
        <div className="cw-top">
          {/* <div className="cw-title">Calendar</div> */}
          <div className="cw-tz">{timeZone}</div>
        </div>

        <div className="cw-main">
          <div className="cw-dayname">{dayName}</div>
          <div className="cw-day">{today.d}</div>
          <div className="cw-month">{monthYear}</div>
        </div>

        <div className="cw-foot">
          <span className="cw-week">
            ISO Week: <b>{isoWeek}</b>
          </span>

          <button
            type="button"
            className="cw-openbtn"
            onClick={() => setOpen(true)}
            aria-label="Open calendar"
            title="Open calendar"
          >
            📅
          </button>
        </div>
      </div>

      {/* MODAL */}
      {open && (
        <div
          className="cw-modal-overlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={() => setOpen(false)}
        >
          <div className="cw-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="cw-modal-head">
              <div>
                <div className="cw-modal-title">Calendar</div>
                <div className="cw-modal-sub">
                  {timeZone} • Today: {pad2(today.d)}/{pad2(today.m)}/{today.y} •
                  ISO Week <b>{isoWeek}</b>
                </div>
              </div>

              <button
                className="cw-x"
                type="button"
                onClick={() => setOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="cw-nav">
              <button className="cw-navbtn" type="button" onClick={prevMonth}>
                ◀
              </button>
              <div className="cw-navtitle">{monthTitle}</div>
              <button className="cw-navbtn" type="button" onClick={nextMonth}>
                ▶
              </button>

              <button className="cw-todaybtn" type="button" onClick={goToday}>
                Today
              </button>
            </div>

            <div className="cw-grid">
              {weekdayHeader().map((h) => (
                <div key={h} className="cw-grid-h">
                  {h}
                </div>
              ))}

              {grid.map((c, idx) => {
                if (!c) {
                  return <div key={`e-${idx}`} className="cw-cell cw-empty" />;
                }

                const todayCls = isTodayCell(c) ? " cw-today" : "";
                const selCls = isSelectedCell(c) ? " cw-selected" : "";

                return (
                  <button
                    key={`${c.y}-${c.m}-${c.d}`}
                    type="button"
                    className={`cw-cell${todayCls}${selCls}`}
                    onClick={() => setSelected(c)}
                    title={`${c.d}/${c.m}/${c.y}`}
                  >
                    {c.d}
                  </button>
                );
              })}
            </div>

            <div className="cw-modal-foot">
              <div className="cw-selectedline">
                Selected:{" "}
                <b>
                  {pad2(selected.d)}/{pad2(selected.m)}/{selected.y}
                </b>{" "}
                (ISO Week{" "}
                <b>{isoWeekNumber(selected.y, selected.m, selected.d)}</b>)
              </div>
              <div className="cw-footnote">
                Month view is computed in the selected timezone.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
